import React, { createContext, useContext, useState, useEffect } from 'react';
import { usePlayer } from './PlayerContext';
import { db } from '../firebase/config';
import { doc, setDoc, collection, query, where, getDocs, getDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';

// Create the context
export const MilestoneContext = createContext();

// Custom hook to use the milestone context
export const useMilestones = () => useContext(MilestoneContext);

// Achievement types
export const ACHIEVEMENT_TYPES = {
  PERFORMANCE: 'performance',
  STREAK: 'streak',
  COMPARATIVE: 'comparative',
  SEASONAL: 'seasonal',
  CAREER: 'career'
};

// Milestone categories
export const MILESTONE_CATEGORIES = {
  GAME: 'game',
  SEASON: 'season',
  CAREER: 'career',
  SPECIAL: 'special'
};

// Milestone types
export const MILESTONE_TYPES = {
  BATTING: 'batting',
  PITCHING: 'pitching'
};

// Provider component
export const MilestoneProvider = ({ children }) => {
  const { playerData, currentSeason } = usePlayer();
  // Default milestone templates based on a 25-game season
  const defaultMilestones = [
    {
      id: 'default-batting-avg-300',
      title: 'Batting .300',
      description: 'Achieve a batting average of .300 or higher for the season',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'battingAvg', operator: '>=', value: '0.300', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-hits-25',
      title: '25 Hit Club',
      description: 'Record 25 hits in a single season',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'hits', operator: '>=', value: '25', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-rbi-15',
      title: 'RBI Machine',
      description: 'Drive in 15 runs in a single season',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'rbi', operator: '>=', value: '15', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-doubles-5',
      title: 'Double Trouble',
      description: 'Hit 5 doubles in a single season',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'doubles', operator: '>=', value: '5', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-game-3hits',
      title: 'Perfect Day',
      description: 'Record 3 or more hits in a single game',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.GAME,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'hits', operator: '>=', value: '3', unit: 'perGame' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-hitting-streak',
      title: 'Hitting Streak',
      description: 'Record at least one hit in 5 consecutive games',
      type: MILESTONE_TYPES.BATTING,
      category: MILESTONE_CATEGORIES.STREAK,
      achievementType: ACHIEVEMENT_TYPES.STREAK,
      criteria: [{ stat: 'hits', operator: '>=', value: '1', unit: 'perGame' }],
      thresholdValue: '5',
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-pitching-win',
      title: 'Winning Pitcher',
      description: 'Record 3 wins in a season',
      type: MILESTONE_TYPES.PITCHING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'wins', operator: '>=', value: '3', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    },
    {
      id: 'default-pitching-strikeouts',
      title: 'Strikeout Artist',
      description: 'Record 15 strikeouts in a season',
      type: MILESTONE_TYPES.PITCHING,
      category: MILESTONE_CATEGORIES.SEASON,
      achievementType: ACHIEVEMENT_TYPES.PERFORMANCE,
      criteria: [{ stat: 'strikeouts', operator: '>=', value: '15', unit: '' }],
      progress: 0,
      achieved: false,
      isDefault: true
    }
  ];
  
  const [milestones] = useState(defaultMilestones);
  const [userMilestones, setUserMilestones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch user-defined milestones from Firestore
  useEffect(() => {
    const fetchUserMilestones = async () => {
      if (!playerData?.id) return;
      
      try {
        setLoading(true);
        // Get the current user ID from the auth context
        const auth = getAuth();
        const uid = auth.currentUser?.uid;
        
        if (!uid) {
          throw new Error("User not authenticated");
        }
        
        const milestonesRef = collection(db, 'users', uid, 'players', playerData.id, 'milestones');
        const q = query(milestonesRef, where("seasonId", "==", currentSeason?.id || 'all'));
        const querySnapshot = await getDocs(q);
        
        const fetchedMilestones = [];
        querySnapshot.forEach((doc) => {
          fetchedMilestones.push({ id: doc.id, ...doc.data() });
        });
        
        setUserMilestones(fetchedMilestones);
      } catch (err) {
        console.error("Error fetching user milestones:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchUserMilestones();
  }, [playerData?.id, currentSeason?.id]);

  // Create a new user-defined milestone
  const createMilestone = async (milestoneData) => {
    if (!playerData?.id) {
      throw new Error("Player data not available");
    }

    try {
      // Get the current user ID from the auth context
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      const milestonesRef = collection(db, 'users', uid, 'players', playerData.id, 'milestones');
      const newMilestoneRef = doc(milestonesRef);
      
      const milestone = {
        ...milestoneData,
        id: newMilestoneRef.id,
        createdAt: new Date(),
        achieved: false,
        progress: 0,
        seasonId: milestoneData.category === 'career' ? 'all' : (currentSeason?.id || 'all')
      };
      
      await setDoc(newMilestoneRef, milestone);
      
      // Update local state
      setUserMilestones(prev => [...prev, milestone]);
      
      return milestone;
    } catch (err) {
      console.error("Error creating milestone:", err);
      throw err;
    }
  };

  // Update a milestone
  const updateMilestone = async (id, updates) => {
    if (!playerData?.id) {
      throw new Error("Player data not available");
    }

    try {
      // Get the current user ID from the auth context
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      const milestoneRef = doc(db, 'users', uid, 'players', playerData.id, 'milestones', id);
      await setDoc(milestoneRef, updates, { merge: true });
      
      // Update local state
      setUserMilestones(prev => 
        prev.map(milestone => 
          milestone.id === id ? { ...milestone, ...updates } : milestone
        )
      );
    } catch (err) {
      console.error("Error updating milestone:", err);
      throw err;
    }
  };

  // Delete a milestone
  const deleteMilestone = async (id) => {
    if (!playerData?.id) {
      throw new Error("Player data not available");
    }

    try {
      // Get the current user ID from the auth context
      const auth = getAuth();
      const uid = auth.currentUser?.uid;
      
      if (!uid) {
        throw new Error("User not authenticated");
      }
      
      const milestoneRef = doc(db, 'users', uid, 'players', playerData.id, 'milestones', id);
      await setDoc(milestoneRef, { deleted: true }, { merge: true });
      
      // Update local state
      setUserMilestones(prev => prev.filter(milestone => milestone.id !== id));
    } catch (err) {
      console.error("Error deleting milestone:", err);
      throw err;
    }
  };

  // Check milestones after game submission
  const checkMilestones = async (gameData) => {
    // This will be called after a game is submitted
    // It will check all milestones and update progress/achievement status

    // Get all milestones (system + user-defined)
    const allMilestones = [...milestones, ...userMilestones];

    console.log("[MILESTONE DEBUG] Checking milestones for game:", {
      gameId: gameData.id,
      date: gameData.date,
      opponent: gameData.opponent,
      battingStats: gameData.battingStats ? {
        atBats: gameData.battingStats.atBats,
        hits: gameData.battingStats.hits
      } : 'No batting stats'
    });

    // Pre-fetch season stats once to avoid multiple Firestore reads
    let cachedSeasonStats = null;
    const hasSeasonMilestones = allMilestones.some(
      m => !m.achieved && (m.category === MILESTONE_CATEGORIES.SEASON || m.category === MILESTONE_CATEGORIES.CAREER)
    );

    if (hasSeasonMilestones && gameData.seasonId && playerData?.id) {
      try {
        const auth = getAuth();
        const uid = auth.currentUser?.uid;

        if (uid) {
          // Wait for stats to be updated (same delay as before)
          await new Promise(resolve => setTimeout(resolve, 5000));

          const seasonStatsRef = doc(db, 'users', uid, 'players', playerData.id, 'seasons', gameData.seasonId);
          const seasonStatsDoc = await getDoc(seasonStatsRef);

          if (seasonStatsDoc.exists()) {
            cachedSeasonStats = seasonStatsDoc.data();
            console.log(`[MILESTONE DEBUG] Pre-fetched season stats for caching:`, {
              games: cachedSeasonStats.battingStats?.games,
              battingAvg: cachedSeasonStats.battingStats?.average
            });
          }
        }
      } catch (error) {
        console.error(`[MILESTONE DEBUG] Error pre-fetching season stats:`, error);
        // Continue without cache - individual milestones will fetch as needed
      }
    }

    // Process each milestone using Promise.all to ensure all async operations complete
    await Promise.all(allMilestones.map(async (milestone) => {
      if (milestone.achieved) return; // Skip already achieved milestones
      
      // For game milestones, check immediately
      if (milestone.category === MILESTONE_CATEGORIES.GAME) {
        try {
          let progress = await calculateMilestoneProgress(milestone, gameData);
          let achieved = progress >= 100;

          console.log(`[MILESTONE DEBUG] Game milestone "${milestone.title}": progress=${progress}, achieved=${achieved}`);

          if (achieved || progress !== milestone.progress) {
            // If milestone is achieved, store game details
            const updates = {
              progress,
              achieved
            };

            if (achieved) {
              const now = new Date();
              updates.achievedDate = now;

              // Store game details for reference
              updates.gameDetails = {
                gameId: gameData.id,
                date: gameData.date,
                opponent: gameData.opponent,
                location: gameData.location,
                isHome: gameData.isHome,
                stats: milestone.type === MILESTONE_TYPES.BATTING
                  ? { ...gameData.battingStats }
                  : { ...gameData.pitchingStats }
              };
            }

            await updateMilestone(milestone.id, updates);
          }
        } catch (error) {
          console.error(`[MILESTONE DEBUG] Error checking game milestone "${milestone.title}":`, error);
        }
      }
      // For season and career milestones, use cached stats to avoid multiple Firestore reads
      else if (milestone.category === MILESTONE_CATEGORIES.SEASON ||
               milestone.category === MILESTONE_CATEGORIES.CAREER) {
        // Note: Stats are already fetched and cached above (with 5s delay)
        try {
          let progress = await calculateMilestoneProgress(milestone, gameData, cachedSeasonStats);
          let achieved = progress >= 100;

          console.log(`[MILESTONE DEBUG] ${milestone.category} milestone "${milestone.title}": progress=${progress}, achieved=${achieved}`);

          if (achieved || progress !== milestone.progress) {
            // If milestone is achieved, store game details
            const updates = {
              progress,
              achieved
            };

            if (achieved) {
              const now = new Date();
              updates.achievedDate = now;

              // Store game details for reference
              updates.gameDetails = {
                gameId: gameData.id,
                date: gameData.date,
                opponent: gameData.opponent,
                location: gameData.location,
                isHome: gameData.isHome,
                stats: milestone.type === MILESTONE_TYPES.BATTING
                  ? { ...gameData.battingStats }
                  : { ...gameData.pitchingStats }
              };
            }

            await updateMilestone(milestone.id, updates);
          }
        } catch (error) {
          console.error(`[MILESTONE DEBUG] Error checking ${milestone.category} milestone "${milestone.title}":`, error);
        }
      }
      // For other milestone types, check immediately
      else {
        try {
          let progress = await calculateMilestoneProgress(milestone, gameData);
          let achieved = progress >= 100;

          console.log(`[MILESTONE DEBUG] Other milestone "${milestone.title}": progress=${progress}, achieved=${achieved}`);

          if (achieved || progress !== milestone.progress) {
            // If milestone is achieved, store game details
            const updates = {
              progress,
              achieved
            };

            if (achieved) {
              const now = new Date();
              updates.achievedDate = now;

              // Store game details for reference
              updates.gameDetails = {
                gameId: gameData.id,
                date: gameData.date,
                opponent: gameData.opponent,
                location: gameData.location,
                isHome: gameData.isHome,
                stats: milestone.type === MILESTONE_TYPES.BATTING
                  ? { ...gameData.battingStats }
                  : { ...gameData.pitchingStats }
              };
            }

            await updateMilestone(milestone.id, updates);
          }
        } catch (error) {
          console.error(`[MILESTONE DEBUG] Error checking other milestone "${milestone.title}":`, error);
        }
      }
    }));
  };

  // Calculate milestone progress based on game data and milestone criteria
  // Optional cachedSeasonStats parameter to avoid excessive Firestore reads
  const calculateMilestoneProgress = async (milestone, gameData, cachedSeasonStats = null) => {
    // Skip if no criteria defined
    if (!milestone.criteria || milestone.criteria.length === 0) {
      return milestone.progress || 0;
    }
    
    // Get the relevant stats based on milestone type
    const gameStats = milestone.type === MILESTONE_TYPES.BATTING 
      ? gameData.battingStats || {}
      : gameData.pitchingStats || {};
    
    // For career milestones, we need to look at career stats
    if (milestone.category === MILESTONE_CATEGORIES.CAREER) {
      // Get career stats from the player data
      const careerStats = milestone.type === MILESTONE_TYPES.BATTING 
        ? playerData?.battingStats || {}
        : playerData?.pitchingStats || {};
      
      // Check if all criteria are met
      const criteriaMet = milestone.criteria.filter(criterion => {
        const statValue = careerStats[criterion.stat];
        if (statValue === undefined) return false;
        
        switch (criterion.operator) {
          case '>=': return statValue >= parseFloat(criterion.value);
          case '>': return statValue > parseFloat(criterion.value);
          case '<=': return statValue <= parseFloat(criterion.value);
          case '<': return statValue < parseFloat(criterion.value);
          case '==': return statValue === parseFloat(criterion.value);
          default: return false;
        }
      });
      
      // Calculate progress as percentage of criteria met
      if (criteriaMet.length === 0) return 0;
      
      // For single criterion milestones, calculate percentage progress
      if (milestone.criteria.length === 1) {
        const criterion = milestone.criteria[0];
        const statValue = careerStats[criterion.stat] || 0;
        const targetValue = parseFloat(criterion.value);
        
        // Calculate progress percentage based on how close we are to the target
        if (criterion.operator === '>=' || criterion.operator === '>') {
          return Math.min(Math.floor((statValue / targetValue) * 100), 100);
        } else if (criterion.operator === '<=' || criterion.operator === '<') {
          // For "less than" criteria, progress increases as the value decreases
          // This is a bit counterintuitive, so we might want to handle differently
          return statValue <= targetValue ? 100 : 0;
        } else {
          // For equality, it's either 100% or 0%
          return statValue === targetValue ? 100 : 0;
        }
      }
      
      // For multiple criteria, use percentage of criteria met
      return Math.floor((criteriaMet.length / milestone.criteria.length) * 100);
    }
    
      // For season milestones, we need to look at season stats
      if (milestone.category === MILESTONE_CATEGORIES.SEASON) {
        // Use cached season stats if available to avoid excessive Firestore reads
        let seasonStats = {};

        if (cachedSeasonStats) {
          // Use the cached stats passed in
          seasonStats = milestone.type === MILESTONE_TYPES.BATTING
            ? cachedSeasonStats.battingStats || {}
            : cachedSeasonStats.pitchingStats || {};
          console.log(`[MILESTONE DEBUG] Using cached season stats for milestone "${milestone.title}"`);
        } else {
          // Fall back to fetching from Firestore only if no cache available
          try {
            const auth = getAuth();
            const uid = auth.currentUser?.uid;

            if (!uid || !playerData?.id || !gameData.seasonId) {
              console.log(`[MILESTONE DEBUG] Missing required data for fetching season stats`);
              return milestone.progress || 0;
            }

            // Fetch the latest season stats directly from Firestore
            const seasonStatsRef = doc(db, 'users', uid, 'players', playerData.id, 'seasons', gameData.seasonId);
            const seasonStatsDoc = await getDoc(seasonStatsRef);

            if (seasonStatsDoc.exists()) {
              const seasonData = seasonStatsDoc.data();
              seasonStats = milestone.type === MILESTONE_TYPES.BATTING
                ? seasonData.battingStats || {}
                : seasonData.pitchingStats || {};
            } else {
              console.log(`[MILESTONE DEBUG] Season stats document does not exist`);
              return milestone.progress || 0;
            }
          } catch (error) {
            console.error(`[MILESTONE DEBUG] Error fetching season stats:`, error);
            return milestone.progress || 0;
          }
        }
        
        // Log detailed information about season stats for debugging
        console.log(`[MILESTONE DEBUG] Season milestone "${milestone.title}" checking:`, {
          seasonId: gameData.seasonId,
          seasonStats: {
            games: seasonStats.games,
            atBats: seasonStats.atBats,
            hits: seasonStats.hits,
            doubles: seasonStats.doubles,
            homeRuns: seasonStats.homeRuns,
            rbi: seasonStats.rbi
          },
          criteria: milestone.criteria
        });
        
        // Check if all criteria are met
        const criteriaMet = milestone.criteria.filter(criterion => {
          const statValue = seasonStats[criterion.stat];
          if (statValue === undefined) {
            console.log(`[MILESTONE DEBUG] Stat "${criterion.stat}" is undefined for milestone "${milestone.title}"`);
            return false;
          }
          
          const criterionValue = parseFloat(criterion.value);
          const result = (() => {
            switch (criterion.operator) {
              case '>=': return statValue >= criterionValue;
              case '>': return statValue > criterionValue;
              case '<=': return statValue <= criterionValue;
              case '<': return statValue < criterionValue;
              case '==': return statValue === criterionValue;
              default: return false;
            }
          })();
          
          console.log(`[MILESTONE DEBUG] Criterion check for "${milestone.title}": ${criterion.stat} (${statValue}) ${criterion.operator} ${criterionValue} = ${result}`);
          return result;
        });
      
      // Calculate progress as percentage of criteria met
      if (criteriaMet.length === 0) return 0;
      
      // For single criterion milestones, calculate percentage progress
      if (milestone.criteria.length === 1) {
        const criterion = milestone.criteria[0];
        const statValue = seasonStats[criterion.stat] || 0;
        const targetValue = parseFloat(criterion.value);
        
        // Calculate progress percentage based on how close we are to the target
        if (criterion.operator === '>=' || criterion.operator === '>') {
          return Math.min(Math.floor((statValue / targetValue) * 100), 100);
        } else if (criterion.operator === '<=' || criterion.operator === '<') {
          // For "less than" criteria, progress increases as the value decreases
          return statValue <= targetValue ? 100 : 0;
        } else {
          // For equality, it's either 100% or 0%
          return statValue === targetValue ? 100 : 0;
        }
      }
      
      // For multiple criteria, use percentage of criteria met
      return Math.floor((criteriaMet.length / milestone.criteria.length) * 100);
    }
    
    // For game milestones, check if the current game meets the criteria
    if (milestone.category === MILESTONE_CATEGORIES.GAME) {
      // Check each criterion
      const allCriteriaMet = milestone.criteria.every(criterion => {
        const statValue = gameStats[criterion.stat];
        if (statValue === undefined) return false;
        
        // Handle per-game unit
        if (criterion.unit === 'perGame') {
          switch (criterion.operator) {
            case '>=': return statValue >= parseFloat(criterion.value);
            case '>': return statValue > parseFloat(criterion.value);
            case '<=': return statValue <= parseFloat(criterion.value);
            case '<': return statValue < parseFloat(criterion.value);
            case '==': return statValue === parseFloat(criterion.value);
            default: return false;
          }
        } else {
          // Regular comparison
          switch (criterion.operator) {
            case '>=': return statValue >= parseFloat(criterion.value);
            case '>': return statValue > parseFloat(criterion.value);
            case '<=': return statValue <= parseFloat(criterion.value);
            case '<': return statValue < parseFloat(criterion.value);
            case '==': return statValue === parseFloat(criterion.value);
            default: return false;
          }
        }
      });
      
      return allCriteriaMet ? 100 : 0;
    }
    
    // For streak milestones, we need to track consecutive games
    if (milestone.achievementType === ACHIEVEMENT_TYPES.STREAK) {
      // Get the threshold value (number of consecutive games required)
      const threshold = parseInt(milestone.thresholdValue || '0');
      if (threshold <= 0) return 0;
      
      // Check if the current game meets the criteria
      const currentGameMeetsCriteria = milestone.criteria.every(criterion => {
        const statValue = gameStats[criterion.stat];
        if (statValue === undefined) return false;
        
        switch (criterion.operator) {
          case '>=': return statValue >= parseFloat(criterion.value);
          case '>': return statValue > parseFloat(criterion.value);
          case '<=': return statValue <= parseFloat(criterion.value);
          case '<': return statValue < parseFloat(criterion.value);
          case '==': return statValue === parseFloat(criterion.value);
          default: return false;
        }
      });
      
      // If the current game doesn't meet criteria, reset progress
      if (!currentGameMeetsCriteria) {
        return 0;
      }
      
      // If the current game meets criteria, increment the streak
      const currentStreak = (milestone.progress || 0) > 0 
        ? Math.floor((milestone.progress * threshold) / 100) + 1
        : 1;
      
      // Calculate progress percentage
      const progress = Math.min(Math.floor((currentStreak / threshold) * 100), 100);
      
      return progress;
    }
    
    // For any other milestone types, return the current progress
    return milestone.progress || 0;
  };

  // Get all milestones (system + user-defined)
  const getAllMilestones = () => {
    return [...milestones, ...userMilestones].filter(m => !m.deleted);
  };

  // Filter milestones by various criteria
  const getFilteredMilestones = (filters = {}) => {
    const { status, type, category, search, achievementType } = filters;
    
    return getAllMilestones().filter(milestone => {
      // Filter by status
      if (status && status !== 'all') {
        if (status === 'achieved' && !milestone.achieved) return false;
        if (status === 'in-progress' && milestone.achieved) return false;
      }
      
      // Filter by type (batting/pitching)
      if (type && type !== 'all' && milestone.type !== type) return false;
      
      // Filter by category (game/season/career/special)
      if (category && category !== 'all' && milestone.category !== category) return false;
      
      // Filter by achievement type
      if (achievementType && achievementType !== 'all' && milestone.achievementType !== achievementType) return false;
      
      // Filter by search query
      if (search && search.trim()) {
        const query = search.toLowerCase();
        const titleMatch = milestone.title?.toLowerCase().includes(query);
        const descMatch = milestone.description?.toLowerCase().includes(query);
        if (!titleMatch && !descMatch) return false;
      }
      
      return true;
    });
  };

  // Sort milestones
  const getSortedMilestones = (milestones, sortBy = 'date') => {
    return [...milestones].sort((a, b) => {
      if (sortBy === 'progress') {
        // Sort by progress percentage (achieved always at 100%)
        const progressA = a.achieved ? 100 : (a.progress || 0);
        const progressB = b.achieved ? 100 : (b.progress || 0);
        
        // Sort descending (highest progress first)
        return progressB - progressA;
      } 
      else if (sortBy === 'date') {
        // Put achieved milestones first
        if (a.achieved && !b.achieved) return -1;
        if (!a.achieved && b.achieved) return 1;
        
        // If both achieved, sort by date (newest first)
        if (a.achieved && b.achieved) {
          // Safety check in case date is missing
          if (!a.achievedDate) return 1;
          if (!b.achievedDate) return -1;
          
          // Convert dates to timestamps and compare
          const dateA = new Date(a.achievedDate);
          const dateB = new Date(b.achievedDate);
          return dateB - dateA;
        }
        
        // If both in progress, sort by progress
        const progressA = a.progress || 0;
        const progressB = b.progress || 0;
        return progressB - progressA;
      } 
      else if (sortBy === 'alphabetical') {
        // Sort by title alphabetically
        const titleA = (a.title || '').toLowerCase();
        const titleB = (b.title || '').toLowerCase();
        return titleA.localeCompare(titleB);
      }
      
      return 0;
    });
  };

  // Value to be provided by the context
  const value = {
    milestones: getAllMilestones(),
    userMilestones,
    loading,
    error,
    createMilestone,
    updateMilestone,
    deleteMilestone,
    checkMilestones,
    getFilteredMilestones,
    getSortedMilestones,
    ACHIEVEMENT_TYPES,
    MILESTONE_CATEGORIES,
    MILESTONE_TYPES
  };

  return (
    <MilestoneContext.Provider value={value}>
      {children}
    </MilestoneContext.Provider>
  );
};

export default MilestoneProvider;
